import React from 'react';
import { Box } from '@mui/material';

// A chessboard made of data, seen low at an angle — the pain section's
// "same mistakes" visual. Every square is a mesh of little vertical bars of
// varied, frozen heights. Purple → cream, in the same language as the Anna/Noah
// portraits.
//
// Nothing moves until the cursor is over the board. Under it the bars grow and
// ripple, and a few squares — your error squares, which at rest look like any
// other — shoot up and turn red, then settle back as the cursor moves on.

const RAMP = [[52, 42, 96], [96, 78, 180], [138, 116, 228], [236, 226, 219]];
const RED = [228, 68, 110];

// 'f7' → { f: 5, r: 1 } (r = 0 is rank 8, the far edge).
const sq = (name) => ({ f: name.charCodeAt(0) - 97, r: 8 - Number(name[1]) });
// Each error square with how many times you went wrong there (shown on hover).
const ERRORS = [
  { at: 'f7', count: 14 },
  { at: 'e5', count: 9 },
  { at: 'g4', count: 23 },
  { at: 'd7', count: 6 },
  { at: 'h6', count: 17 },
  { at: 'f3', count: 11 },
];
const ERROR_SQUARES = ERRORS.map((e) => sq(e.at));

// Camera + layout. yaw turns the board on its diagonal; pitch tilts it (larger =
// lower, more side-on); focal sets perspective (smaller = stronger); scale is
// how many board units fit across the canvas (smaller = bigger board); cx/cy
// place the board's centre (fractions of the canvas); gap is the empty border
// inside each square. Values picked by eye with a drag-to-orient tuner.
export const DEFAULT_CAMERA = {
  yaw: 0.79,
  pitch: 1.212,
  focal: 17,
  scale: 4.9,
  cx: -0.1,
  cy: 0.53,
  gap: 0.17,
};

function mulberry(seed) {
  return function random() {
    seed |= 0;
    seed = (seed + 0x6d2b79f5) | 0;
    let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function rampColor(n, out) {
  const t = Math.pow(Math.min(1, Math.max(0, n)), 0.9);
  let a; let b; let k;
  if (t < 0.4) { a = RAMP[0]; b = RAMP[1]; k = t / 0.4; }
  else if (t < 0.75) { a = RAMP[1]; b = RAMP[2]; k = (t - 0.4) / 0.35; }
  else { a = RAMP[2]; b = RAMP[3]; k = (t - 0.75) / 0.25; }
  out[0] = a[0] + (b[0] - a[0]) * k;
  out[1] = a[1] + (b[1] - a[1]) * k;
  out[2] = a[2] + (b[2] - a[2]) * k;
}

// Board space (u across files, v across ranks, y up) → camera space.
function project(u, y, v, cam) {
  const X = u - 4;
  const Z = v - 4;
  const cy = Math.cos(cam.yaw); const sy = Math.sin(cam.yaw);
  const x1 = X * cy - Z * sy;
  const z1 = X * sy + Z * cy;
  const cp = Math.cos(cam.pitch); const sp = Math.sin(cam.pitch);
  const y2 = y * sp - z1 * cp;
  const z2 = y * cp + z1 * sp;
  const s = cam.focal / Math.max(0.5, cam.focal - z2);
  return { x: x1 * s, y: -y2 * s, s, depth: z2 };
}

const SQUARE_NAME = (f, r) => `${String.fromCharCode(97 + f)}${8 - r}`;

function buildBoard(cam) {
  const random = mulberry(2026);
  const errorIndex = new Map(ERROR_SQUARES.map((e, i) => [`${e.f},${e.r}`, i]));
  const bars = [];
  const squares = [];
  const k = 5;
  // Optional empty border inside each square (cam.gap); 0 = bars run evenly
  // across the whole board.
  const inset = cam.gap;
  const span = 1 - inset * 2;
  for (let f = 0; f < 8; f += 1) {
    for (let r = 0; r < 8; r += 1) {
      const light = (f + r) % 2 === 0;
      const err = errorIndex.has(`${f},${r}`) ? errorIndex.get(`${f},${r}`) : -1;
      squares.push({
        f, r, light, err,
        name: SQUARE_NAME(f, r),
        centre: project(f + 0.5, 0, r + 0.5, cam),
      });
      // Same density on every square; light vs dark is colour and height.
      for (let i = 0; i < k; i += 1) {
        for (let j = 0; j < k; j += 1) {
          const u = f + inset + ((i + 0.5) / k) * span;
          const v = r + inset + ((j + 0.5) / k) * span;
          bars.push({
            ...project(u, 0, v, cam),
            u,
            v,
            n: (light ? 0.74 : 0.3) + random() * 0.14,
            err,
            phase: random() * Math.PI * 2,
            bar: (light ? 0.35 : 0.22) + random() * random() * (light ? 0.8 : 0.5),
          });
        }
      }
    }
  }
  bars.sort((a, b) => a.depth - b.depth);
  return { bars, squares };
}

// headroom: transparent px at the top of the canvas, above the board's own area,
// so hover labels on the far squares have room. cy is measured below it.
// footroom: transparent px at the bottom, so the board's near corner can run
// past the layout box (e.g. over the next section) instead of being cut.
export default function DataBoard({ camera = DEFAULT_CAMERA, headroom = 0, footroom = 0, sx }) {
  const canvasRef = React.useRef(null);
  // Live camera for the render loop; the geometry is rebuilt when it changes.
  const camRef = React.useRef(camera);
  camRef.current = camera;

  React.useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;

    let builtCam = null;
    let bars = [];
    let squares = [];
    let errorSquares = [];
    const rebuild = () => {
      builtCam = camRef.current;
      ({ bars, squares } = buildBoard(builtCam));
      errorSquares = squares.filter((q) => q.err >= 0).sort((a, b) => a.err - b.err);
    };
    // 0 → 1 per error square: how revealed (tall + red) it is right now.
    const reveal = new Float32Array(ERROR_SQUARES.length);
    let raf = null;
    let disposed = false;
    let visible = true;
    let width = 0;
    let height = 0;
    let dpr = 1;
    const pointer = { x: 0, y: 0, last: -1e9 };

    let reduceMotion = false;
    try { reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch (e) { /* keep motion */ }

    // Decorative: the lens follows the real cursor without the canvas taking events.
    const onPointerMove = (e) => { pointer.x = e.clientX; pointer.y = e.clientY; pointer.last = performance.now(); };
    window.addEventListener('pointermove', onPointerMove, { passive: true });

    let observer = null;
    try {
      observer = new IntersectionObserver((entries) => {
        visible = entries.some((en) => en.isIntersecting);
      }, { rootMargin: '120px' });
      observer.observe(canvas);
    } catch (e) { /* always render */ }

    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      const nextDpr = Math.min(window.devicePixelRatio || 1, 2);
      const w = Math.max(1, Math.round(rect.width));
      const h = Math.max(1, Math.round(rect.height));
      if (w === width && h === height && nextDpr === dpr) return;
      width = w; height = h; dpr = nextDpr;
      canvas.width = Math.round(w * dpr);
      canvas.height = Math.round(h * dpr);
    };

    const rgb = [0, 0, 0];
    let lensX = 0;
    let lensY = 0;
    let lensOn = 0;
    let hovered = null;
    let labelA = 0;
    const draw = (time) => {
      raf = requestAnimationFrame(draw);
      if (disposed || !visible) return;
      resize();
      if (camRef.current !== builtCam) rebuild();
      const cam = builtCam;
      const ctx = canvas.getContext('2d');
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, width, height);

      // One board unit on screen; the board is wider than the canvas on purpose so
      // its left part runs off the edge.
      const unit = width / cam.scale;
      const cx = width * cam.cx;
      const cy = headroom + (height - headroom - footroom) * cam.cy;
      const t = reduceMotion ? 0 : time / 1000;

      // Lens: only the cursor, only while it's over the board. Its strength
      // eases in and out so the board settles back to still smoothly.
      const rect = canvas.getBoundingClientRect();
      const px = pointer.x - rect.left;
      const py = pointer.y - rect.top;
      const cursorHere = !reduceMotion && time - pointer.last < 2500
        && px > 0 && py > 0 && px < width && py < height;
      if (cursorHere) {
        lensX += (px - lensX) * 0.35;
        lensY += (py - lensY) * 0.35;
      }
      lensOn += ((cursorHere ? 1 : 0) - lensOn) * (cursorHere ? 0.15 : 0.05);
      const lensR = width * 0.15;
      const lensR2 = lensR * lensR;
      const lensAt = (x, y) => {
        if (lensOn < 0.001) return 0;
        const dx = x - lensX; const dy = y - lensY;
        const d2 = dx * dx + dy * dy;
        if (d2 >= lensR2) return 0;
        const f = 1 - Math.sqrt(d2) / lensR;
        return f * f * (3 - 2 * f) * lensOn;
      };

      // Error squares ease toward revealed while the lens is on them, and fade back.
      for (let e = 0; e < errorSquares.length; e += 1) {
        const ce = errorSquares[e].centre;
        const target = reduceMotion ? 0 : Math.min(1, lensAt(cx + ce.x * unit, cy + ce.y * unit) * 1.6);
        const rate = target > reveal[e] ? 0.14 : 0.035;
        reveal[e] += (target - reveal[e]) * rate;
      }

      const sp = Math.sin(cam.pitch);
      for (let i = 0; i < bars.length; i += 1) {
        const b = bars[i];
        const x = cx + b.x * unit;
        const y = cy + b.y * unit;
        const grow = lensAt(x, y);
        const rv = b.err >= 0 ? reveal[b.err] : 0;

        // Frozen wave pattern at rest; under the lens the bars ripple.
        const wave = 0.45 + 0.55 * (0.5 + 0.5 * Math.sin(b.u * 0.85 + b.v * 0.55 + b.phase * 0.35))
          + (grow > 0 ? grow * 0.35 * Math.sin(t * 4 + b.phase) : 0);
        const w = Math.max(0.8, 0.12 * unit * b.s * 0.36 * (1 + 0.4 * grow + 0.5 * rv));
        const h = b.bar * wave * 0.12 * unit * b.s * 3.2 * sp * (1 + 1.1 * grow) * (1 + 0.9 * rv);

        rampColor(b.n + 0.45 * grow, rgb);
        let alpha = Math.min(1, (0.16 + 0.66 * Math.pow(b.n, 0.8)) * (1 + 0.55 * grow));
        if (rv > 0.001) {
          rgb[0] += (RED[0] - rgb[0]) * rv;
          rgb[1] += (RED[1] - rgb[1]) * rv;
          rgb[2] += (RED[2] - rgb[2]) * rv;
          alpha += (1 - alpha) * rv;
        }
        alpha *= 0.55 + 0.45 * Math.min(1, b.s);
        ctx.fillStyle = `rgba(${rgb[0] | 0},${rgb[1] | 0},${rgb[2] | 0},${alpha.toFixed(3)})`;
        ctx.fillRect(x - w / 2, y - h, w, h);
      }

      // Square name under the cursor, floating above its square.
      if (lensOn > 0.01) {
        let best = null; let bestD = Infinity;
        for (let q = 0; q < squares.length; q += 1) {
          const ce = squares[q].centre;
          const dx = cx + ce.x * unit - lensX; const dy = cy + ce.y * unit - lensY;
          const d = dx * dx + dy * dy;
          if (d < bestD) { bestD = d; best = squares[q]; }
        }
        if (best && Math.sqrt(bestD) < unit * 0.75 * best.centre.s) {
          if (best !== hovered) { hovered = best; labelA = 0; }
        } else hovered = null;
      } else hovered = null;
      labelA += ((hovered ? 1 : 0) - labelA) * 0.18;
      if (hovered && labelA > 0.01) {
        const ce = hovered.centre;
        const bx = cx + ce.x * unit;
        const by = cy + ce.y * unit;
        const err = hovered.err >= 0;
        const text = err ? `${hovered.name} · ${ERRORS[hovered.err].count} mistakes` : hovered.name;
        // Error squares' bars spike tall, so their label floats higher.
        const lift = unit * (err ? 1.45 : 0.9) * ce.s;
        const rise = (1 - labelA) * unit * 0.15;
        const ly = by - lift + rise;
        ctx.save();
        ctx.globalAlpha = labelA;
        // Thin stem from the pill down to the square.
        ctx.strokeStyle = err ? 'rgba(228,68,110,0.7)' : 'rgba(236,226,219,0.45)';
        ctx.lineWidth = 1;
        // Mid-size pill, capped so it doesn't keep growing with the board.
        const fs = Math.round(Math.min(26, Math.max(18, unit * 0.16)));
        const ph = Math.round(fs * 1.9);
        ctx.beginPath(); ctx.moveTo(bx, ly + ph / 2); ctx.lineTo(bx, by); ctx.stroke();
        ctx.font = `700 ${fs}px Urbanist, sans-serif`;
        const tw = ctx.measureText(text).width;
        const pw = tw + ph * 0.9;
        ctx.fillStyle = err ? 'rgba(228,68,110,0.95)' : 'rgba(22,22,28,0.9)';
        ctx.strokeStyle = err ? 'rgba(255,255,255,0.25)' : 'rgba(236,226,219,0.2)';
        ctx.beginPath();
        if (ctx.roundRect) ctx.roundRect(bx - pw / 2, ly - ph / 2, pw, ph, ph / 2);
        else ctx.rect(bx - pw / 2, ly - ph / 2, pw, ph);
        ctx.fill(); ctx.stroke();
        ctx.fillStyle = err ? '#fff' : 'rgb(236,226,219)';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(text, bx, ly + 1);
        ctx.restore();
      }
    };
    raf = requestAnimationFrame(draw);

    return () => {
      disposed = true;
      if (raf) cancelAnimationFrame(raf);
      if (observer) observer.disconnect();
      window.removeEventListener('pointermove', onPointerMove);
    };
  }, [headroom, footroom]);

  return <Box component="canvas" ref={canvasRef} aria-hidden="true" sx={sx} />;
}
